/**
 * KpiTile — one commercial KPI: icon, label, figure, benchmark line.
 *
 * Lifted out of `hotel/portfolio/page.tsx` when the P3 home band (CD-1) became
 * its second consumer. The band condenses the drill-in's proven pieces, so the
 * two surfaces must render a KPI identically — a second local copy is how the
 * resort's ECOR ends up formatted one way on home and another on the dashboard.
 *
 * Atlas: I1, B1.
 */
import type { ReactNode } from "react";
import Link from "next/link";
import { useT } from "@/lib/locale";

interface Props {
  icon: ReactNode;
  label: string;
  value: string;
  sub: string;
  /** Present when the KPI drills into its own page; adds the hover affordance. */
  href?: string;
  /** Token pair for the icon chip — semantic role decides it, per surface. */
  colour: string;
}

export function KpiTile({ icon, label, value, sub, href, colour }: Props) {
  const t = useT();
  const inner = (
    <>
      <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${colour}`}>
        {icon}
      </span>
      <p className="text-xs text-neutral-500 mt-2">{label}</p>
      <p className="text-xl font-display font-bold text-neutral-900">{value}</p>
      <p className="text-xs text-neutral-500">{sub}</p>
    </>
  );

  if (href) {
    return (
      <Link href={href} className="card-hover flex flex-col p-4 group">
        {inner}
        <span className="text-xs text-brand-primary-text mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-fast">
          {t("hotel.portfolio.viewDetails")}
        </span>
      </Link>
    );
  }

  return <div className="card-surface flex flex-col p-4">{inner}</div>;
}
